import { Device, DeviceConnectivityResult, DeviceSyncStatus } from '@vex/model/device';
import { Injectable } from '@angular/core';
import { BaseService, IPaginatorService } from './base';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BasePaginator } from '../model/base/base-model';
import { Observable, of } from 'rxjs';
import { tap, finalize, share } from 'rxjs/operators';
import { DeploymentValidationSummary } from '@vex/model/deployment-validation';
@Injectable({
  providedIn: 'root'
})
export class DeviceService extends IPaginatorService {
  readonly baseEndpoint = 'Device';
  private cache: any = <any>{};
  private cachedObservable: Observable<any> = <Observable<any>>{};

  constructor(private http: HttpClient) {
    super();
  }

  syncDevice(id) {
    return this.http.get(this.baseUrl + this.baseEndpoint + '/Sync/' + id);
  }

  checkNameAvailibility(name: string): Observable<string[]> {
    return this.http.get<string[]>(
      this.baseUrl + this.baseEndpoint + '/CheckName/' + encodeURIComponent(name)
    );
  }

  getAllVersionsByRecordGroupId(recordGroupId: string): Observable<any[]> {
    return this.http.get<any[]>(this.baseUrl + this.baseEndpoint + '/GetAllVersionsByRecordGroupId/' + recordGroupId);
  }

  getDevice(): Observable<Device[]> {
    return this.http.get<Device[]>(this.baseUrl + this.baseEndpoint + '/');
  }

  getDeviceForSelect(): Observable<any[]> {
    return this.http.get<any[]>(this.baseUrl + this.baseEndpoint + '/GetForSelect');
  }

  getForSelectWithDataCenterDetails(): Observable<any[]> {
    return this.http.get<any[]>(
      this.baseUrl + this.baseEndpoint + '/GetForSelectWithDataCenterDetails'
    );
  }

  getForSelectPaginated(param): Observable<any[]> {
    let url = this.baseUrl + this.baseEndpoint + '/GetForSelectPaginatedPost';
    return this.http.post<any[]>(url, param);
  }

  checkJobConflicts(param): Observable<any[]> {
    let url = this.baseUrl + this.baseEndpoint + '/CheckJobConflicts';
    return this.http.post<any[]>(url, param);
  }

  checkJobConflictsV2(param): Observable<any> {
    const url = this.baseUrl + this.baseEndpoint + '/CheckJobConflictsV2';
    return this.http.post<any>(url, param);
  }

  getPaginatedData<Device>(
    sort: string,
    order: string,
    page: number,
    size: number,
    filter: string,
    params: number
  ): Observable<BasePaginator<Device>> {
    let url =
      this.baseUrl + this.baseEndpoint + '/GetPaginated/' + params + '/' + page + '/' + size + '?';

    if (sort && order) {
      url += `sort=${sort}&order=${order}`;
    }
    if (filter) {
      url += (sort ? '&' : '') + `filter=${filter}`;
    }

    return this.http.get<BasePaginator<Device>>(url);
  }

  updateloadImage(deviceId: string, file: File) {
    const formData = new FormData();
    formData.append('deviceId', deviceId);
    formData.append('file', file, file.name);

    const params = new HttpParams();

    const options = {
      params,
      reportProgress: false
    };

    return this.http.post<Device>(
      this.baseUrl + this.baseEndpoint + '/UploadDeviceImage',
      formData,
      options
    );
  }

  uploadConfigFile(deviceId: string, commitMessage: string, file: File, preserveCurrentVersion: boolean = false) {
    const formData = new FormData();
    formData.append('deviceId', deviceId);
    formData.append('commitMessage', commitMessage);
    formData.append('preserveCurrentVersion', preserveCurrentVersion.toString());
    formData.append('file', file, file.name);

    const params = new HttpParams();

    const options = {
      params,
      reportProgress: false
    };

    return this.http.post<Device>(
      this.baseUrl + this.baseEndpoint + '/UploadDeviceConfiguration',
      formData,
      options
    );
  }

  applyDeploymentToDeviceInventoryBluePrint(inventories: any[]) {
    return this.http.post(this.baseUrl + this.baseEndpoint + '/ApplyDeploymentToDeviceInventory', inventories);
  }

  getDeviceFilteredResourcesByJob(inventories: any[]) {
    return this.http.post(this.baseUrl + this.baseEndpoint + '/GetDeviceFilteredResourcesByJob', inventories);
  }

  applyDeploymentToDeviceInventoryBluePrintV3(inventories: any[]) {
    return this.http.post(this.baseUrl + this.baseEndpoint + '/ApplyDeploymentToDeviceInventoryV3', inventories);
  }

  getPaginatedPostData(filter: any) {
    return this.http.post<any>(this.baseUrl + this.baseEndpoint + '/GetPaginatedPost', filter);
  }

  getCatalogSummary(filter: any) {
    return this.http.post<any>(this.baseUrl + this.baseEndpoint + '/GetCatalogSummary', filter);
  }

  parseCatalogQuery(query: string) {
    return this.http.post<any>(this.baseUrl + this.baseEndpoint + '/ParseCatalogQuery', { Query: query });
  }

  translateCatalogQuery(prompt: string) {
    return this.http.post<{ SuggestedQuery: string }>(
      this.baseUrl + this.baseEndpoint + '/TranslateCatalogQuery',
      { Query: prompt }
    );
  }

  getDeviceById(Id: string): Observable<Device> {
    return this.http.get<Device>(this.baseUrl + this.baseEndpoint + '/GetById/' + Id);
  }

  getDeviceSummaryById(Id: string): Observable<Device> {
    return this.http.get<Device>(this.baseUrl + this.baseEndpoint + '/GetSummaryById/' + Id);
  }

  getDeviceSyncStatus(Id: string): Observable<DeviceSyncStatus> {
    return this.http.get<DeviceSyncStatus>(this.baseUrl + this.baseEndpoint + '/GetSyncStatus/' + Id);
  }

  getDeviceForEdit(Id: string): Observable<Device> {
    return this.http.get<Device>(this.baseUrl + this.baseEndpoint + '/GetForEdit/' + Id);
  }

  getDeviceConfigurationById(Id: string): Observable<{ RawConfig: string; ParseConfig: string }> {
    return this.http.get<{ RawConfig: string; ParseConfig: string }>(
      this.baseUrl + this.baseEndpoint + '/GetConfigurationById/' + Id
    );
  }

  getServiceDetailsByDeviceId(Id: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + this.baseEndpoint + '/GetServiceDetailsByDeviceId/' + Id);
  }

  getStateByDeviceId(devices: any): Observable<any> {
    return this.http.post<Device[]>(this.baseUrl + this.baseEndpoint + '/CheckDeviceState', devices);
  }

  /** Re-evaluates one exact, previously collected command execution rather than selecting a changing latest run. */
  recomputeDeviceState(device: any): Observable<any> {
    const deviceId = typeof device === 'string' ? device : device?.Id;
    const executionRunId = typeof device === 'string' ? null : device?.ExecutionRunId;
    return this.http.post<any>(this.baseUrl + this.baseEndpoint + '/RecomputeDeviceState',
      { DeviceId: deviceId, ExecutionRunId: executionRunId });
  }

  createDevice(device: Device): Observable<Device> {
    return this.http.post<Device>(this.baseUrl + this.baseEndpoint + '', device);
  }

  importDevice(devices: Device[]): Observable<Device[]> {
    return this.http.post<Device[]>(this.baseUrl + this.baseEndpoint + '/BulkImport', devices);
  }

  checkNames(names: string[]): Observable<string[]> {
    return this.http.post<string[]>(this.baseUrl + this.baseEndpoint + '/CheckNames', names);
  }

  checkJobConflictsForResources(request: any): Observable<Device[]> {
    return this.http.post<Device[]>(this.baseUrl + this.baseEndpoint + '/CheckJobConflictsForResources', request);
  }

  deploymentValidationSummary(request: any): Observable<DeploymentValidationSummary> {
    return this.http.post<DeploymentValidationSummary>(this.baseUrl + this.baseEndpoint + '/DeploymentValidationSummary', request);
  }

  updateDevice(device: Device): Observable<Device> {
    return this.http.put<Device>(
      this.baseUrl + this.baseEndpoint + '/' + device.Id + '?id=' + device.Id,
      device
    );
  }

  deleteDevice(id: string): Observable<Device> {
    return this.http.delete<Device>(this.baseUrl + this.baseEndpoint + '/' + id);
  }

  deleteDevices(device: any[]): Observable<any[]> {
    return this.http.post<any[]>(this.baseUrl + this.baseEndpoint + '/DeleteDevices', device);
  }

  checkDeviceConnenctivityById(deviceId: string): Observable<DeviceConnectivityResult> {
    return this.http.get<DeviceConnectivityResult>(
      this.baseUrl + this.baseEndpoint + '/CheckConnectivity/' + deviceId
    );
  }

  checkDevicePullConfigurationById(deviceId: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + this.baseEndpoint + '/PullDeviceConfig/' + deviceId);
  }

  forceParseConfiguration(deviceId: string): Observable<any> {
    return this.http.post<any>(
      this.baseUrl + this.baseEndpoint + '/ForceParseConfiguration/' + deviceId,
      {}
    );
  }

  getHistory(recordGroupId: string) {
    return this.http.get<any[]>(this.baseUrl + this.baseEndpoint + '/GetAllVersions/' + recordGroupId);
    // let observable: Observable<any>;
    // if (this.cache[recordGroupId]) {
    //   observable = of(this.cache[recordGroupId]);
    // } else if (this.cachedObservable[recordGroupId]) {
    //   observable = this.cachedObservable[recordGroupId];
    // } else {
    //   this.cachedObservable[recordGroupId] = this.http
    //     .get<any[]>(this.baseUrl + this.baseEndpoint + '/GetAllVersions/' + recordGroupId)
    //     .pipe(
    //       tap((res) => (this.cache[recordGroupId] = res)),
    //       share(),
    //       finalize(() => (this.cachedObservable[recordGroupId] = null))
    //     );
    //   observable = this.cachedObservable[recordGroupId];
    // }
    // return observable;
  }

  generateRollback(record_grpup_id: string, version: string) {
    return this.http.get<any>(
      this.baseUrl + this.baseEndpoint + '/GenerateRollback/'+record_grpup_id+'/'+version
    );
  }

  getDeviceInventoryHistory(source: string, target: string) {
    return this.http.get<any>(
      this.baseUrl + this.baseEndpoint + '/GetComparision/' + target + '/' + source
    );
  }

  getRawConfigByInventoryId(id): Observable<any> {
    return this.http.get<any>(
      this.baseUrl + this.baseEndpoint + '/GetRawConfigByInventoryId/' + id,
      { responseType: 'text' as 'json' }
    );
  }

  getTerraformCodeById(id): Observable<any> {
    return this.http.get<any>(
      this.baseUrl + this.baseEndpoint + '/GenerateTerraformCodeByInventoryId/' + id
    );
  }

  rollBackInventory(recordGroupId, version) {
    return this.http.get(
      this.baseUrl + this.baseEndpoint + '/Rollback/' + recordGroupId + '/' + version
    );
  }

  pullDeviceConfigByIntegration(payLoad: any): Observable<any> {
    let url = this.baseUrl + this.baseEndpoint + '/PullDeviceConfigByIntegration';
    return this.http.post<any[]>(url, payLoad);
  }

  getComplianceParametersName(params) {
    let url = this.baseUrl + this.baseEndpoint + '/GetComplianceParametersName';
    return this.http.post<any>(url, params);
  }

  getComplianceParametersNameLabel(params){
    let url = this.baseUrl + this.baseEndpoint + '/GetComplianceParametersNameLabel'
    return this.http.post<any>(url, params);
  }

  getAzureDeviceData(date): Observable<any[]> {
    return this.http.get<any[]>(this.baseUrl + 'CloudData/Azure' + '/GetComparedLldpData?time='+date);
  }

  getDeviceByIds(deviceIds: string[]): Observable<any> {
    return this.http.post<string[]>(this.baseUrl + this.baseEndpoint + '/GetByIds', deviceIds);
  }
  
  getDefaultBlueprint(): Observable<any> {
    return this.http.get<any>(this.baseUrl + this.baseEndpoint + '/GetDefaultDCBlueprint');
  }

  getCommandsByDeviceIds(deviceIds: any) {
    return this.http.post(this.baseUrl + this.baseEndpoint + '/GetCommandIdsByDeviceIds', deviceIds);
  }

  /** Starts command collection for a device; the response identifies the correlated assessment run. */
  refreshDeviceState(device:any){
    const deviceId = typeof device === 'string' ? device : device?.Id;
    return this.http.post<any>(this.baseUrl + this.baseEndpoint + `/RefreshDeviceState/${deviceId}`, {});
  }

  /** Assesses the latest parsed state evidence, queueing policy commands only when no evidence exists. */
  assessDeviceState(deviceId: string, benchmarkIds: string[]) {
    return this.http.post<{ RunId: string; QueuedRefresh: boolean }>(
      this.baseUrl + this.baseEndpoint + `/AssessDeviceState/${deviceId}`,
      { BenchmarkIds: benchmarkIds });
  }
}
